import React, { useState, useEffect } from 'react';
import { useOrganization } from '../context/OrganizationContext';
import { useProfile } from '../context/userContext';
import { useNavigate } from 'react-router-dom';
import { useAuth0 } from '@auth0/auth0-react';
import DashboardStyles from '../styles/Dashboard.module.css';
import { Box, Card, CardContent, Typography, Grid, Chip, LinearProgress, CircularProgress } from '@mui/material';
import BusinessIcon from '@mui/icons-material/Business';
import PeopleIcon from '@mui/icons-material/People';
import DroneIcon from '../components/DroneIcon';
import AssessmentIcon from '@mui/icons-material/Assessment';
import VerifiedUserIcon from '@mui/icons-material/VerifiedUser';
import Battery80Icon from '@mui/icons-material/Battery80';
import CameraAltIcon from '@mui/icons-material/CameraAlt';
import ReadOnlyBanner from '../components/organization/ReadOnlyBanner';
import { RecentActivityWidget } from '../components/activity';

/**
 * Dashboard - Organization Overview (Placeholder)
 *
 * Shows organization subscription details and coming soon features.
 * Full dashboard with fleet status, team activity, and compliance will be implemented in Phase 6-7.
 */
function Dashboard() {
    const navigate = useNavigate();
    const { profile, loading: profileLoading } = useProfile();
    const { getAccessTokenSilently } = useAuth0();
    const {
        currentOrganization,
        userRole,
        subscriptionLimits,
        subscriptionStatus,
        isOrganizationUser,
        loading: orgLoading
    } = useOrganization();

    // State for fleet counts
    const [fleetCounts, setFleetCounts] = useState({
        drones: 0,
        batteries: 0,
        payloads: 0,
        teamMembers: 0
    });
    const [fleetLoading, setFleetLoading] = useState(true);

    // Fetch fleet counts
    useEffect(() => {
        const fetchFleetCounts = async () => {
            if (!currentOrganization?.organizationUuid) return;

            try {
                setFleetLoading(true);
                const token = await getAccessTokenSilently();
                const orgUuid = currentOrganization?.organizationUuid;

                // Fetch drones, batteries, payloads, and team members in parallel
                const [dronesRes, batteriesRes, payloadsRes, membersRes] = await Promise.all([
                    fetch(`${process.env.REACT_APP_API_URL}/api/organization/${orgUuid}/drones`, {
                        headers: { Authorization: `Bearer ${token}` }
                    }),
                    fetch(`${process.env.REACT_APP_API_URL}/api/organization/${orgUuid}/batteries`, {
                        headers: { Authorization: `Bearer ${token}` }
                    }),
                    fetch(`${process.env.REACT_APP_API_URL}/api/organization/${orgUuid}/payloads`, {
                        headers: { Authorization: `Bearer ${token}` }
                    }),
                    fetch(`${process.env.REACT_APP_API_URL}/api/organization/${orgUuid}/members`, {
                        headers: { Authorization: `Bearer ${token}` }
                    })
                ]);

                const [dronesData, batteriesData, payloadsData, membersData] = await Promise.all([
                    dronesRes.ok ? dronesRes.json() : { drones: [] },
                    batteriesRes.ok ? batteriesRes.json() : { batteries: [] },
                    payloadsRes.ok ? payloadsRes.json() : { payloads: [] },
                    membersRes.ok ? membersRes.json() : { members: [] }
                ]);

                setFleetCounts({
                    drones: dronesData.drones?.length || 0,
                    batteries: batteriesData.batteries?.length || 0,
                    payloads: payloadsData.payloads?.length || 0,
                    teamMembers: membersData.activeCount || 0  // Only count active members, not pending invitations
                });
            } catch (error) {
            } finally {
                setFleetLoading(false);
            }
        };

        fetchFleetCounts();
    }, [currentOrganization, getAccessTokenSilently]);

    // Show loading state
    if (profileLoading || orgLoading) {
        return (
            <Box display="flex" justifyContent="center" alignItems="center" minHeight="80vh">
                <CircularProgress />
            </Box>
        );
    }

    // Redirect individual users to home
    if (!isOrganizationUser) {
        navigate('/');
        return null;
    }

    // No organization selected
    if (!currentOrganization) {
        return (
            <Box className={DashboardStyles.container}>
                <Typography variant="h5" color="error">
                    No organization selected
                </Typography>
            </Box>
        );
    }

    const { maxDrones, maxBatteries, maxPayloads } = subscriptionLimits;

    // Role display mapping
    const roleColors = {
        owner: 'error',
        admin: 'warning',
        manager: 'info',
        pilot: 'success',
        viewer: 'default'
    };

    // Subscription status color
    const statusColors = {
        active: 'success',
        trialing: 'info',
        past_due: 'warning',
        canceled: 'error',
        incomplete: 'warning'
    };

    return (
        <div className={DashboardStyles.container}>
            {/* Read-only Mode Banner */}
            <ReadOnlyBanner />

            {/* Header Section */}
            <Box className={DashboardStyles.header}>
                <Box display="flex" alignItems="center" gap={{ xs: 1, sm: 2 }} flexWrap="wrap">
                    <BusinessIcon sx={{ fontSize: { xs: 32, sm: 40 }, color: 'white', flexShrink: 0 }} />
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                        <Typography
                            variant="h4"
                            component="h1"
                            sx={{
                                fontSize: { xs: '1.25rem', sm: '1.5rem', md: '2rem' },
                                wordBreak: 'break-word'
                            }}
                        >
                            {currentOrganization.organizationName}
                        </Typography>
                        <Box display="flex" gap={0.5} mt={1} flexWrap="wrap">
                            <Chip
                                label={currentOrganization.subscription?.planName || 'No Plan'}
                                color="primary"
                                size="small"
                                sx={{ fontSize: { xs: '0.7rem', sm: '0.75rem' } }}
                            />
                            <Chip
                                label={subscriptionStatus || 'Unknown'}
                                color={statusColors[subscriptionStatus] || 'default'}
                                size="small"
                                sx={{ fontSize: { xs: '0.7rem', sm: '0.75rem' } }}
                            />
                            <Chip
                                label={`Role: ${userRole}`}
                                color={roleColors[userRole] || 'default'}
                                size="small"
                                sx={{ fontSize: { xs: '0.7rem', sm: '0.75rem' } }}
                            />
                        </Box>
                    </Box>
                </Box>
            </Box>

            {/* Fleet Limits Section */}
            <Box className={DashboardStyles.section}>
                <Typography variant="h6" gutterBottom sx={{ fontSize: { xs: '1rem', sm: '1.25rem' } }}>
                    Fleet Management Limits
                </Typography>
                <Grid container spacing={{ xs: 2, sm: 3 }}>
                    <Grid item xs={12} sm={6} md={4}>
                        <Card elevation={2}>
                            <CardContent>
                                <Box display="flex" alignItems="center" gap={1} mb={2}>
                                    <DroneIcon color="primary" sx={{ fontSize: { xs: 20, sm: 24 } }} />
                                    <Typography variant="h6" sx={{ fontSize: { xs: '1rem', sm: '1.25rem' } }}>Drones</Typography>
                                </Box>
                                <Typography variant="h3" color="primary" sx={{ fontSize: { xs: '1.75rem', sm: '2.5rem', md: '3rem' } }}>
                                    {fleetLoading ? '-' : fleetCounts.drones} / {maxDrones}
                                </Typography>
                                <LinearProgress
                                    variant="determinate"
                                    value={fleetLoading ? 0 : (fleetCounts.drones / maxDrones) * 100}
                                    sx={{ mt: 2, height: 8, borderRadius: 4 }}
                                />
                                <Typography variant="caption" color="textSecondary" mt={1}>
                                    {maxDrones - fleetCounts.drones} drones available
                                </Typography>
                            </CardContent>
                        </Card>
                    </Grid>

                    <Grid item xs={12} sm={6} md={4}>
                        <Card elevation={2}>
                            <CardContent>
                                <Box display="flex" alignItems="center" gap={1} mb={2}>
                                    <Battery80Icon color="primary" sx={{ fontSize: { xs: 20, sm: 24 } }} />
                                    <Typography variant="h6" sx={{ fontSize: { xs: '1rem', sm: '1.25rem' } }}>Batteries</Typography>
                                </Box>
                                <Typography variant="h3" color="primary" sx={{ fontSize: { xs: '1.75rem', sm: '2.5rem', md: '3rem' } }}>
                                    {fleetLoading ? '-' : fleetCounts.batteries} / {maxBatteries}
                                </Typography>
                                <LinearProgress
                                    variant="determinate"
                                    value={fleetLoading ? 0 : (fleetCounts.batteries / maxBatteries) * 100}
                                    sx={{ mt: 2, height: 8, borderRadius: 4 }}
                                />
                                <Typography variant="caption" color="textSecondary" mt={1}>
                                    {maxBatteries - fleetCounts.batteries} batteries available
                                </Typography>
                            </CardContent>
                        </Card>
                    </Grid>

                    <Grid item xs={12} sm={6} md={4}>
                        <Card elevation={2}>
                            <CardContent>
                                <Box display="flex" alignItems="center" gap={1} mb={2}>
                                    <CameraAltIcon color="primary" sx={{ fontSize: { xs: 20, sm: 24 } }} />
                                    <Typography variant="h6" sx={{ fontSize: { xs: '1rem', sm: '1.25rem' } }}>Payloads</Typography>
                                </Box>
                                <Typography variant="h3" color="primary" sx={{ fontSize: { xs: '1.75rem', sm: '2.5rem', md: '3rem' } }}>
                                    {fleetLoading ? '-' : fleetCounts.payloads} / {maxPayloads}
                                </Typography>
                                <LinearProgress
                                    variant="determinate"
                                    value={fleetLoading ? 0 : (fleetCounts.payloads / maxPayloads) * 100}
                                    sx={{ mt: 2, height: 8, borderRadius: 4 }}
                                />
                                <Typography variant="caption" color="textSecondary" mt={1}>
                                    {maxPayloads - fleetCounts.payloads} payloads available
                                </Typography>
                            </CardContent>
                        </Card>
                    </Grid>
                </Grid>
            </Box>

            {/* Team & Activity Section */}
            <Box className={DashboardStyles.section}>
                <Grid container spacing={{ xs: 2, sm: 3 }}>
                    {/* Team Members Card */}
                    <Grid item xs={12} sm={6} md={4}>
                        <Card elevation={2} sx={{ height: '100%' }}>
                            <CardContent>
                                <Box display="flex" alignItems="center" gap={1} mb={2}>
                                    <PeopleIcon color="primary" sx={{ fontSize: { xs: 20, sm: 24 } }} />
                                    <Typography variant="h6" sx={{ fontSize: { xs: '1rem', sm: '1.25rem' } }}>Team Members</Typography>
                                </Box>
                                <Typography variant="h3" color="primary" sx={{ fontSize: { xs: '1.75rem', sm: '2.5rem', md: '3rem' } }}>
                                    {fleetLoading ? '-' : fleetCounts.teamMembers}
                                </Typography>
                                <Typography variant="caption" color="textSecondary" mt={1} display="block">
                                    Active members in your organization
                                </Typography>
                            </CardContent>
                        </Card>
                    </Grid>

                    {/* Recent Activity Widget */}
                    <Grid item xs={12} sm={6} md={8}>
                        <RecentActivityWidget
                            orgId={currentOrganization?.organizationUuid}
                            limit={8}
                            onViewAll={() => navigate(`/organization/${currentOrganization?.organizationUuid}/activities`)}
                            title="Recent Activity"
                            height="300px"
                        />
                    </Grid>
                </Grid>
            </Box>

            {/* Coming Soon Features */}
            <Box className={DashboardStyles.section}>
                <Typography variant="h6" gutterBottom sx={{ fontSize: { xs: '1rem', sm: '1.25rem' } }}>
                    Coming Soon
                </Typography>
                <Grid container spacing={{ xs: 2, sm: 3 }}>
                    <Grid item xs={12} md={6}>
                        <Card elevation={1} className={DashboardStyles.comingSoonCard}>
                            <CardContent>
                                <Box display="flex" alignItems="center" gap={1} mb={2}>
                                    <PeopleIcon color="disabled" />
                                    <Typography variant="h6" color="textSecondary">
                                        Team Management
                                    </Typography>
                                </Box>
                                <Typography variant="body2" color="textSecondary">
                                    View and manage team members, roles, and permissions. Invite new members to your organization.
                                </Typography>
                                <Chip label="Phase 6" size="small" sx={{ mt: 2 }} />
                            </CardContent>
                        </Card>
                    </Grid>

                    <Grid item xs={12} md={6}>
                        <Card elevation={1} className={DashboardStyles.comingSoonCard}>
                            <CardContent>
                                <Box display="flex" alignItems="center" gap={1} mb={2}>
                                    <DroneIcon color="disabled" />
                                    <Typography variant="h6" color="textSecondary">
                                        Fleet Overview
                                    </Typography>
                                </Box>
                                <Typography variant="body2" color="textSecondary">
                                    Real-time fleet status, maintenance schedules, and asset utilization tracking.
                                </Typography>
                                <Chip label="Phase 7" size="small" sx={{ mt: 2 }} />
                            </CardContent>
                        </Card>
                    </Grid>

                    <Grid item xs={12} md={6}>
                        <Card elevation={1} className={DashboardStyles.comingSoonCard}>
                            <CardContent>
                                <Box display="flex" alignItems="center" gap={1} mb={2}>
                                    <VerifiedUserIcon color="disabled" />
                                    <Typography variant="h6" color="textSecondary">
                                        Compliance Dashboard
                                    </Typography>
                                </Box>
                                <Typography variant="body2" color="textSecondary">
                                    Track pilot licenses, waivers, authorizations, and pre-flight checklists across your team.
                                </Typography>
                                <Chip label="Phase 6" size="small" sx={{ mt: 2 }} />
                            </CardContent>
                        </Card>
                    </Grid>

                    <Grid item xs={12} md={6}>
                        <Card elevation={1} className={DashboardStyles.comingSoonCard}>
                            <CardContent>
                                <Box display="flex" alignItems="center" gap={1} mb={2}>
                                    <AssessmentIcon color="disabled" />
                                    <Typography variant="h6" color="textSecondary">
                                        Analytics & Reports
                                    </Typography>
                                </Box>
                                <Typography variant="body2" color="textSecondary">
                                    Flight analytics, utilization reports, cost analysis, and compliance metrics.
                                </Typography>
                                <Chip label="Phase 6" size="small" sx={{ mt: 2 }} />
                            </CardContent>
                        </Card>
                    </Grid>
                </Grid>
            </Box>

            {/* Quick Start Guide */}
            <Box className={DashboardStyles.section}>
                <Card elevation={2} sx={{ backgroundColor: '#f5f5f5', padding: { xs: 1.5, sm: 2 } }}>
                    <Typography variant="h6" gutterBottom sx={{ fontSize: { xs: '1rem', sm: '1.25rem' } }}>
                        Getting Started
                    </Typography>
                    <Typography variant="body2" paragraph>
                        Welcome to your organization dashboard! Here's what you can do now:
                    </Typography>
                    <ul>
                        <li>
                            <Typography variant="body2">
                                <strong>Create LAANC Operations:</strong> Use the main map (Home) to plan and submit drone operations
                            </Typography>
                        </li>
                        <li>
                            <Typography variant="body2">
                                <strong>View Plans:</strong> Check your subscription details and upgrade options in Settings
                            </Typography>
                        </li>
                        <li>
                            <Typography variant="body2">
                                <strong>Coming Soon:</strong> Fleet management, team collaboration, and compliance tools
                            </Typography>
                        </li>
                    </ul>
                </Card>
            </Box>
        </div>
    );
}

export default Dashboard;
